import { PermitTemplate } from '@core/entities/permit/PermitTemplate'
import { ChangeRequest } from '@core/entities/permit/ChangeRequest'
import { PermitApprovalReviews } from '@core/entities/permit/PermitApprovalReviews'
import {
  ITemplateProjectLocations,
  TemplateProjectLocations,
} from '@core/entities/template/TemplateProjectLocations'
import { Entity } from '@core/entities/entity'
import { IPermitBase } from '@core/entities/permit/PermitBase'
import { plainToInstance, Type } from 'class-transformer'
import moment from 'moment/moment'
import {
  IPermitApprovalWrapper,
  PermitApprovalWrapper,
} from '@core/entities/permit/PermitApprovalWrapper'
import { PermitNominateApprover } from '@core/entities/permit/PermitNominateApprover'
import { PermitTransfer } from '@core/entities/permit/PermitTransfer'
import { localizedStrings } from '@core/strings'
import { User } from '@core/entities/user'
import { Participant } from '@core/entities/participant/Participant'
import { Feature, FeatureCollection, GeoJSON, Point, Polygon } from 'geojson'
import { PermitFinalSignOffReviews } from '../PermitFinalSignOffReviews'
import { PermitFinalSignOffReview } from '../PermitFinalSignOffReview'
import { PermitStatus } from './types'
import { CompanySummary } from '@core/entities/company'
import {
  getGeolocationAreas,
  getW3wLocation,
} from '@core/utils/geolocation-helpers'
import { Comments } from '@core/entities/comments'
import { statuses } from '@core/theme/statuses'

export class PermitBase<
  ExtendedType extends IPermitBase = IPermitBase,
> extends Entity<ExtendedType> {
  id: number
  name: string
  uuid: string
  shortUUID: string
  project: { id: number; name: string; timezone: string }
  @Type(() => PermitTemplate)
  template: PermitTemplate
  organisationId: number
  location: string
  locationId?: string
  geoJSON?: GeoJSON
  summary: string
  status: PermitStatus
  startsOn: string
  endsOn: string
  createdOn: string
  @Type(() => User)
  permitHolder: User
  @Type(() => User)
  permitAuthor: User
  @Type(() => Participant)
  participants: Array<Participant>
  countdownExpiry?: string
  countdownName?: string
  @Type(() => ChangeRequest)
  lastRequestedChange?: ChangeRequest
  @Type(() => PermitTransfer)
  lastTransfer?: PermitTransfer
  projectCounterCode?: string
  @Type(() => PermitApprovalReviews)
  approvalReviews?: PermitApprovalReviews
  @Type(() => PermitFinalSignOffReviews)
  finalSignOffReviews: PermitFinalSignOffReviews
  @Type(() => TemplateProjectLocations)
  projectLocations: TemplateProjectLocations

  //needed for the form to work - API sync would be nice
  projectId?: number
  projectName?: string
  templateId?: number
  templateVersionId?: number
  draftId?: number
  draftName?: string
  permitHolderId?: number
  exportResult?: {
    url: string
    directUrl: string
    key: string
  }
  @Type(() => CompanySummary)
  company?: CompanySummary
  comments: Comments

  // NOTE: currentTemplate is only present in the outdated permit drafts and should
  // be moved to PermitDraft entity. Added here because react query hooks for fetching
  // drafts are using this entity instead of PermitDraft
  @Type(() => PermitTemplate)
  currentTemplate?: PermitTemplate

  isParentPermit?: boolean

  parentStatusGate?: 'AWAITING_OPEN'

  public static new(data: unknown): PermitBase {
    const entity = plainToInstance<PermitBase, unknown>(PermitBase, data)

    return entity
  }

  public getDuration(): string {
    const duration = moment.duration(moment(this.endsOn).diff(this.startsOn))

    const diffYears = duration.years()
    const diffMonths = duration.months()
    const diffDays = duration.days()
    const diffHours = duration.hours()
    const diffMins = duration.minutes()

    const parts = []
    if (diffYears) {
      parts.push(`${diffYears}${localizedStrings.yearsShort}`)
    }
    if (diffMonths) {
      parts.push(`${diffMonths}${localizedStrings.monthsShort}`)
    }
    if (diffDays) {
      parts.push(`${diffDays}${localizedStrings.daysShort}`)
    }
    if (diffHours) {
      parts.push(`${diffHours}${localizedStrings.hoursShort}`)
    }
    if (diffMins) {
      parts.push(`${diffMins}${localizedStrings.minsShort}`)
    }

    const durationString = parts.join(' ')

    return durationString
  }

  public isPermitCountdownActive(): boolean {
    return !!this.countdownExpiry && moment().isBefore(this.countdownExpiry)
  }

  public isAwaitingChanges(): boolean {
    return (
      this.status === 'REQUESTED' &&
      this.lastRequestedChange?.requestedAt &&
      !this.lastRequestedChange.addressedBy
    )
  }

  public isAwaitingParentOpen(): boolean {
    return this.parentStatusGate === 'AWAITING_OPEN'
  }

  public isAwaitingChangesFromPermitHolder(): boolean {
    return (
      this.template.currentVersion.initiatorRole === 'PERMIT_HOLDER' &&
      this.approvalReviews.nextApprovalStage === 'AFTER_PERMIT_HOLDER'
    )
  }

  public isAwaitingChangesFromAuthPersonBeforePermitHolder(): boolean {
    return (
      this.template.currentVersion.initiatorRole === 'AUTHORISED_PERSON' &&
      this.approvalReviews.nextApprovalStage === 'BEFORE_PERMIT_HOLDER'
    )
  }

  public isAwaitingChangesFromAuthPersonAfterPermitHolder(): boolean {
    return (
      this.template.currentVersion.initiatorRole === 'AUTHORISED_PERSON' &&
      this.approvalReviews.nextApprovalStage === 'AFTER_PERMIT_HOLDER'
    )
  }

  public getRemainingCountdown(): string {
    if (!this.countdownExpiry) return
    let newCountdownString = ''

    const nowMoment = moment()
    const expiryMoment = moment(this.countdownExpiry)

    if (nowMoment.isAfter(expiryMoment)) return

    const duration = moment.duration(expiryMoment.diff(nowMoment))

    if (duration.days()) {
      newCountdownString += `${duration.days()}${localizedStrings.daysShort} `
    }
    if (duration.hours()) {
      newCountdownString += `${duration.hours()}${localizedStrings.hoursShort} `
    }
    if (duration.minutes()) {
      newCountdownString += `${duration.minutes()}${
        localizedStrings.minsShort
      } `
    }
    if (duration.seconds()) {
      newCountdownString += `${duration.seconds()}${
        localizedStrings.secondsShort
      }`
    }

    return newCountdownString
  }

  public getNextApproval(): PermitApprovalWrapper {
    const { nextApprovalId, approvals } = this.approvalReviews || {}
    return approvals.find(({ approval }) => approval.id === nextApprovalId)
  }

  public getNextFinalSignOff(): PermitFinalSignOffReview {
    const { nextFinalSignOffId, reviews } = this.finalSignOffReviews || {}
    return reviews?.find(
      ({ finalSignOff }) => finalSignOff.id === nextFinalSignOffId,
    )
  }

  public getNextNominatedApprovers(): Array<PermitNominateApprover> {
    const nextNominatedApprovers =
      this?.approvalReviews?.nominatedApprovers?.approvers?.filter(
        ({ targetApproval }) => {
          return targetApproval.id === this.approvalReviews.nextApprovalId
        },
      ) ?? []

    return nextNominatedApprovers
  }

  /**
   * Returns nominated approvers for a given approval (by id).
   * Use for future / not-yet-submitted approvals to show who is nominated.
   */
  public getNominatedApproversForApproval(
    approvalId: string,
  ): Array<PermitNominateApprover> {
    return (
      this.approvalReviews?.nominatedApprovers?.approvers?.filter(
        ({ targetApproval }) => targetApproval.id === approvalId,
      ) ?? []
    )
  }

  /**
   * Returns comma-separated names of nominated approvers for a given approval.
   * Empty string if none. Use for displaying pending approval assignees.
   */
  public getNominatedApproverNamesForApproval(approvalId: string): string {
    const nominatedApprovers = this.getNominatedApproversForApproval(approvalId)
    return nominatedApprovers
      .map(({ targetApprovers }) =>
        targetApprovers.map((approver) => approver.name),
      )
      .flat()
      .join(', ')
  }

  public getReviews(
    action?: IPermitApprovalWrapper['action'],
    approvals?: Array<PermitApprovalWrapper>,
  ): Array<PermitApprovalWrapper> {
    const list = approvals || this.approvalReviews?.approvals || []
    return (
      list.filter(
        (approval) =>
          !!approval.action && (!action || approval.action === action),
      ) || []
    )
  }

  public getStatusChipColor() {
    return PermitBase.getStatusChipColor(this.status)
  }

  public static getStatusChipColor(status: PermitStatus) {
    switch (status) {
      case 'REQUESTED':
      case 'AWAITING_FINAL_SIGN_OFF':
      case 'AWAITING_CHECKS':
      case 'SENT_TO_PERMIT_HOLDER':
      case 'SUSPENDED_RESUME_REQUESTED':
      case 'EXTENSION_REQUESTED':
        return statuses.approval.color
      case 'ISSUED':
        return statuses.preStart.color
      case 'CANCELLED':
        return statuses.cancelled.color
      case 'REJECTED':
        return statuses.rejected.color
      case 'EXPIRED':
        return statuses.expired.color
      case 'UNSIGNED':
      case 'DISCARDED':
      case 'SUSPENDED':
        return statuses.discarded.color
      case 'OPEN':
      case 'SIGNED':
        return statuses.active.color
      case 'CLOSED':
        return statuses.closed.color
      default:
        return 'resolved'
    }
  }

  public getStatusString(): string {
    return PermitBase.getStatusString(this.status)
  }

  public static getStatusString(status: PermitStatus): string {
    switch (status) {
      case 'REQUESTED':
        // We have mapped the Requested state to Awaiting Approval on the front-end
        // This will be renamed in the back-end in the future
        return statuses.approval.label
      case 'REJECTED':
        return statuses.rejected.label
      case 'DISCARDED':
        return statuses.discarded.label
      case 'ISSUED':
        return statuses.preStart.label
      case 'EXPIRED':
        return statuses.expired.label
      case 'AWAITING_CHECKS':
        return localizedStrings.awaitingPostPermitChecks
      case 'AWAITING_FINAL_SIGN_OFF':
        return localizedStrings.awaitingFinalSignOff
      case 'CLOSED':
        return statuses.closed.label
      case 'UNSIGNED':
        return localizedStrings.unsigned
      case 'SIGNED':
        return localizedStrings.signed
      case 'EXTENSION_REQUESTED':
        return localizedStrings.requestedExtension
      case 'CANCELLED':
        return statuses.cancelled.label
      case 'OPEN':
        return localizedStrings.open
      case 'SENT_TO_PERMIT_HOLDER':
        return localizedStrings.sentToPermitHolder
      case 'SUSPENDED':
        return localizedStrings.suspended
      case 'SUSPENDED_RESUME_REQUESTED':
        return localizedStrings.resumeRequested
      default:
        return status
    }
  }

  public getSecondaryStatusString(): string {
    if (
      this.lastTransfer?.status === 'PENDING' ||
      this.lastTransfer?.status === 'AWAITING_REVIEW'
    ) {
      return this.lastTransfer?.statusText()
    }
    if (this.status !== 'REQUESTED') {
      if (
        this.getNextFinalSignOff() &&
        this.finalSignOffReviews.reviews.length > 1
      ) {
        return this.getNextFinalSignOff().finalSignOff.label
      }
    } else {
      if (this.lastRequestedChange) {
        return this.isAwaitingChanges()
          ? localizedStrings.changesRequested
          : localizedStrings.changesDone
      }
      if (this.approvalReviews?.hasNominatedApprovers()) {
        return this.getNextNominatedApprovers()
          ?.map(({ targetApprovers }) => {
            return targetApprovers.map((approver) => approver.name).join(', ')
          })
          .join(', ')
      }
      if (this.approvalReviews?.approvals.length > 1) {
        return this.getNextApproval()?.approval.label
      }
    }
  }

  public getSecondaryStatusOptions(): Array<{
    label: string
    completed: boolean
    user?: User
  }> {
    if (this.approvalReviews?.approvals.length > 1) {
      return this.approvalReviews.approvals.map((approval) => {
        const approvalId = approval.approval.id
        const labelPostfix =
          this.getNominatedApproverNamesForApproval(approvalId)

        return {
          label:
            approval.submittedBy?.getFullName() ||
            `${approval.approval.label}${
              labelPostfix ? ` - ${labelPostfix}` : ''
            }`,
          completed: approval.action === 'APPROVED',
          user: approval.submittedBy,
        }
      })
    }
    if (this.finalSignOffReviews?.reviews?.length > 1) {
      return this.finalSignOffReviews.reviews.map((review) => ({
        label: review.finalSignOff.label,
        completed: review.finalSignOff?.finalSignOffAnswers?.isAnswered(),
      }))
    }
  }

  getW3wLocation(): Feature<Point> {
    return getW3wLocation(this.geoJSON)
  }

  getGeolocationAreas(): FeatureCollection<Polygon> {
    return getGeolocationAreas(this.geoJSON)
  }

  public getProjectLocationsAreasString(): string {
    const selectedAreasNames = this?.projectLocations?.areas?.flatMap((area) =>
      area.selectedAreas?.map((selectedArea) => selectedArea.name),
    )
    const projectLocationsAreasString = selectedAreasNames
      ?.map((name, index) =>
        index !== selectedAreasNames.length - 1 ? name + ',' : name,
      )
      ?.join(' ')

    return projectLocationsAreasString
  }

  public isApprovalDraftsEnabled(): boolean {
    return this.template?.currentVersion?.allowApprovalDrafts
  }

  /**
   * Fields a child permit should inherit from this parent when the parent
   * is selected during child-permit creation. Returns only the fields that
   * actually exist on the parent so callers can copy them defensively
   * without overwriting form state with undefined.
   *
   * Per the parent-child spec, a child inherits:
   *   - `locations` from the parent's selected project locations (no
   *     areas/pins — those belong to the previous location selection and
   *     should be reset by the caller when applying)
   *   - `startsOn` / `endsOn` as a sensible default date range
   */
  public getChildInheritedFields(): {
    startsOn?: string
    endsOn?: string
    locations?: ITemplateProjectLocations['locations']
  } {
    return {
      startsOn: this.startsOn || undefined,
      endsOn: this.endsOn || undefined,
      locations: this.projectLocations?.locations?.length
        ? this.projectLocations.locations
        : undefined,
    }
  }

  public getApprovalProgressText() {
    const allApprovals = this.approvalReviews?.approvals
    const doneReviews = this.getReviews('APPROVED', allApprovals).length
    const allReviews = allApprovals?.length

    if (
      this.status === 'REQUESTED' &&
      allReviews > 0 &&
      (!this.lastRequestedChange || this.lastRequestedChange?.addressedBy)
    ) {
      return localizedStrings.approvalProgressText(doneReviews, allReviews)
    } else {
      return false
    }
  }

  public getFinalSignOffProgressText(): string | null {
    const allReviews = this.finalSignOffReviews?.reviews?.length
    const doneReviews = this.finalSignOffReviews?.getDoneReviewsIndex()

    if (this.status === 'AWAITING_FINAL_SIGN_OFF' && allReviews > 1) {
      return localizedStrings.finalSignOffProgressText(doneReviews, allReviews)
    }

    return null
  }

  public getActiveParticipants() {
    return (
      this.participants?.filter(
        (participant) =>
          participant.status === 'ACTIVE' &&
          // Filter out permit creator if he's not briefed
          !!participant.roles?.[0],
      ) ?? []
    )
  }

  /**
   * Whether briefings on this entity must collect a participant signature.
   *
   * Permit templates expose this as an opt-out flag (`requiresBriefingSignature`
   * on the current template version). It is treated as `true` when the flag is
   * missing/undefined to preserve the legacy signature-collection flow for any
   * template/version that pre-dates the setting.
   *
   * Mirrored on `Checklist` (where it is always `true`) so briefing screens and
   * modals shared between permits and checklists can call
   * `entity.requiresBriefingSignature()` polymorphically without an
   * `instanceof Permit` check or poking into the template version union.
   */
  public requiresBriefingSignature(): boolean {
    return this.template?.currentVersion?.requiresBriefingSignature !== false
  }

  /**
   * Whether participants added remotely (no in-person signed briefing) must
   * explicitly accept the briefing before they count as briefed.
   *
   * Only meaningful when `requiresBriefingSignature()` is `false` — the server
   * forces the flag off whenever signatures are required. Mirrored on
   * `Checklist` (always `false`) for the same polymorphic reason as
   * `requiresBriefingSignature()`.
   */
  public requiresBriefingAcceptance(): boolean {
    return (
      !this.requiresBriefingSignature() &&
      this.template?.currentVersion?.requiresBriefingAcceptance === true
    )
  }

  public shouldShowNominateApprovers(): boolean {
    const suggestedApprovers =
      this?.approvalReviews?.nominatedApprovers?.suggestedApprovers
    return (
      !!suggestedApprovers?.length &&
      !this?.approvalReviews?.nextApprovalSharedInboxConfigured
    )
  }
}
